import { datadogLogs } from '@datadog/browser-logs';
import { QueryClient } from '@tanstack/react-query';

import { LOGGER_TYPE } from './types';

// Maximum number of retries for 5XX errors
const MAX_RETRIES = 3;

// Check if retries are enabled via environment variable
const isRetryEnabled = process.env.NEXT_PUBLIC_ENABLE_REQUEST_RETRY === 'true';

const customRetry = (failureCount: number, error: unknown): boolean => {
  // If retries are disabled via env var, don't retry
  if (!isRetryEnabled) {
    return false;
  }

  // Only retry up to MAX_RETRIES times
  if (failureCount >= MAX_RETRIES) {
    // Log that all retries have been exhausted
    if (error instanceof Error && 'status' in error) {
      const httpError = error as any;
      const ddLogger = datadogLogs.getLogger(LOGGER_TYPE.QUERY_CLIENT);

      if (ddLogger) {
        ddLogger.error('All retry attempts exhausted for GET request', {
          error_message: httpError.message,
          status_code: httpError.status,
          attempt_count: failureCount,
          max_retries: MAX_RETRIES,
        });
      }
    }
    return false;
  }

  // Only retry on 5XX errors
  if (error instanceof Error && 'status' in error) {
    const httpError = error as any;

    if (httpError.status >= 500 && httpError.status < 600) {
      const ddLogger = datadogLogs.getLogger(LOGGER_TYPE.QUERY_CLIENT);

      // Log the retry attempt
      if (ddLogger) {
        ddLogger.warn('Retrying GET request due to 5XX error', {
          error_message: httpError.message,
          status_code: httpError.status,
          attempt: failureCount + 1,
          max_retries: MAX_RETRIES,
          retry_info: `Attempt ${failureCount + 1} of ${MAX_RETRIES}`,
        });
      }

      return true;
    }
  }

  return false;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 300000,
      staleTime: 60000,
      retry: customRetry,
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, 30000), // exponential backoff
    },
  },
});

export default queryClient;
